import { Box, Flex, Image, Text, keyframes } from '@chakra-ui/react';
import useNowPlaying from 'hooks/use-now-playing';
import ScrollingText from 'components/NowPlaying/ScrollingText';
import hanaRave from 'assets/images/emotes/o87Rave.gif';
import { useEffect, useRef, useState } from 'react';

const pulse = keyframes`
  0%, 100% { transform: scaleY(0.35); }
  50% { transform: scaleY(1); }
`;

const lightSweep = keyframes`
  0% { opacity: 0; transform: translateX(-100%) skewX(-18deg); }
  25% { opacity: 1; }
  75% { opacity: 0.7; }
  100% { opacity: 0; transform: translateX(460px) skewX(-18deg); }
`;

const halo = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 #f2b99b00; }
  35% { box-shadow: 0 0 0 5px #f2b99b30, 0 0 22px 5px #f2b99b55; }
`;

const songReveal = keyframes`
  from { opacity: 0; transform: translateY(4px); }
  to { opacity: 1; transform: translateY(0); }
`;

const NightNowPlaying = () => {
  const { songName, singer } = useNowPlaying();
  const songKey = JSON.stringify([songName, singer]);
  const previousSong = useRef(songKey);
  const [transition, setTransition] = useState<string | null>(null);
  const changingSong = transition === songKey;

  useEffect(() => {
    if (previousSong.current === songKey) return;
    previousSong.current = songKey;
    setTransition(songName ? songKey : null);
  }, [songKey, songName]);

  useEffect(() => {
    if (!transition) return;
    const timeout = window.setTimeout(() => setTransition(null), 1500);
    return () => window.clearTimeout(timeout);
  }, [transition]);

  const details = (
    <Box w="100%" minW={0}>
      <ScrollingText
        fontSize="18px"
        fontWeight="bold"
        lineHeight="26px"
        color="#fff4e9"
      >
        {songName || 'Waiting for a song'}
      </ScrollingText>
      {singer && (
        <ScrollingText
          fontSize="14px"
          lineHeight="20px"
          color="#dbc0bd"
        >
          {singer}
        </ScrollingText>
      )}
    </Box>
  );

  return (
    <Flex
      fontFamily="'Yomogi', cursive"
      w="360px"
      h="112px"
      p={4}
      gap={4}
      align="center"
      bg="linear-gradient(120deg, #372b36, #201d29)"
      border="1px solid #ba8777"
      borderRadius="24px"
      overflow="hidden"
      position="relative"
    >
      <Flex
        boxSize="68px"
        flexShrink={0}
        align="center"
        justify="center"
        bg="#f1d4c5"
        border="3px solid #6d5055"
        borderRadius="full"
        overflow="hidden"
        animation={changingSong ? `${halo} 1400ms ease-out both` : undefined}
        sx={{ '@media (prefers-reduced-motion: reduce)': { animation: 'none' } }}
      >
        <Image
          src={hanaRave}
          boxSize="62px"
          objectFit="contain"
          alt="Hana raving"
        />
      </Flex>
      <Box flex={1} minW={0} overflow="hidden" whiteSpace="nowrap">
        <Flex align="center" justify="space-between" mb={1}>
          <Text fontSize="10px" letterSpacing="1px" fontWeight="bold" color="#f2b99b">
            {songName ? 'NOW PLAYING' : 'READY TO SING'}
          </Text>
          <Flex key={songKey} align="flex-end" h="12px" gap="3px" aria-hidden="true">
            {[0, 0.3, 0.15, 0.45].map((delay, index) => (
              <Box
                key={index}
                w="3px"
                h="12px"
                bg="#f2b99b"
                borderRadius="full"
                transformOrigin="bottom"
                animation={songName ? `${pulse} ${changingSong ? 0.35 : 1}s ${delay}s ease-in-out infinite` : undefined}
                boxShadow={changingSong ? '0 0 6px #f2b99b80' : undefined}
                sx={{ '@media (prefers-reduced-motion: reduce)': { animation: 'none' } }}
              />
            ))}
          </Flex>
        </Flex>
        <Box
          key={songKey}
          animation={changingSong ? `${songReveal} 450ms ease-out both` : undefined}
          sx={{ '@media (prefers-reduced-motion: reduce)': { animation: 'none' } }}
        >
          {details}
        </Box>
      </Box>
      {changingSong && (
        <Box
          key={`night-sweep:${songKey}`}
          data-night-transition="true"
          position="absolute"
          top={0}
          bottom={0}
          left={0}
          w="90px"
          bg="linear-gradient(90deg, transparent, #f2b99b24, #fff4e92b, transparent)"
          pointerEvents="none"
          aria-hidden="true"
          animation={`${lightSweep} 1400ms ease-in-out both`}
          sx={{ '@media (prefers-reduced-motion: reduce)': { display: 'none' } }}
        />
      )}
    </Flex>
  );
};

export default NightNowPlaying;
